import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type Level = 'high' | 'medium' | 'low';
export type Position = 'resistant' | 'neutral' | 'supportive';

/** One person's part in one project. */
export interface StakeholderRole {
  projectId: string;
  projectName: string;
  portfolio: string | null;
  role: string | null;
  influence: Level | null;
  interest: Level | null;
  position: Position | null;
  strategy: string | null;
  cadence: string | null;
}

/** A person, with every project they appear in. */
export interface StakeholderPerson {
  key: string;
  name: string;
  /** Highest influence on any project. */
  influence: Level;
  interest: Level | null;
  /** The most concerning position on any project. */
  position: Position | null;
  /** Most common role, shown on the card. */
  primaryRole: string | null;
  roles: StakeholderRole[];
  projectCount: number;
  portfolios: string[];
}

const RANK: Record<Level, number> = { high: 3, medium: 2, low: 1 };
const POSITION_RANK: Record<Position, number> = { resistant: 3, neutral: 2, supportive: 1 };

const level = (v: unknown): Level | null => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return s === 'high' || s === 'medium' || s === 'low' ? s : null;
};
const position = (v: unknown): Position | null => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return s === 'resistant' || s === 'neutral' || s === 'supportive' ? s : null;
};
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

interface Row { project_id: string; name: string | null; role: string | null; influence: string | null; interest: string | null; position: string | null; strategy: string | null; cadence: string | null }

const PAGE = 5000;

/**
 * Stakeholders as people. The same person usually sits on several projects, so rows read
 * from project documents (waiting or already added) are folded into one card per person
 * with their part on each project. "Add all" puts every detected row in the register.
 */
export function useStakeholderDirectory() {
  const [rows, setRows] = useState<Array<StakeholderRole & { name: string; added: boolean }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [candRes, itemsRes, addedRes] = await Promise.all([
      supabase.from('document_candidates').select('dossier_key, matched_project_id').not('dossier_key', 'is', null).not('matched_project_id', 'is', null).range(0, PAGE - 1),
      supabase.from('project_intel_items').select('dossier_key, payload').eq('kind', 'stakeholder').eq('status', 'suggested').range(0, PAGE - 1),
      supabase.from('project_stakeholders').select('project_id, name, role, influence, interest, position, strategy, cadence').range(0, PAGE - 1),
    ]);
    const dossierProject = new Map<string, string>();
    for (const c of candRes.data ?? []) if (c.dossier_key && c.matched_project_id) dossierProject.set(c.dossier_key, c.matched_project_id);
    const added = (addedRes.data ?? []) as Row[];
    const detected = (itemsRes.data ?? []) as Array<{ dossier_key: string; payload: Record<string, unknown> }>;

    const projectIds = [...new Set([...dossierProject.values(), ...added.map((a) => a.project_id)])];
    const projects = new Map<string, { name: string; portfolio: string | null }>();
    if (projectIds.length > 0) {
      const { data } = await supabase.from('projects_sync').select('id, name, portfolio_id').in('id', projectIds.slice(0, 1000));
      for (const p of data ?? []) projects.set(p.id, { name: p.name, portfolio: p.portfolio_id ?? null });
    }

    const out: Array<StakeholderRole & { name: string; added: boolean }> = [];
    const push = (projectId: string | undefined, name: string | null, r: Omit<Row, 'project_id' | 'name'>, isAdded: boolean) => {
      if (!projectId || !name) return;
      const proj = projects.get(projectId);
      out.push({
        name, added: isAdded, projectId, projectName: proj?.name ?? 'Unnamed project', portfolio: proj?.portfolio ?? null,
        role: text(r.role), influence: level(r.influence), interest: level(r.interest), position: position(r.position),
        strategy: text(r.strategy), cadence: text(r.cadence),
      });
    };
    for (const a of added) push(a.project_id, text(a.name), a, true);
    for (const d of detected) {
      const p = d.payload;
      push(dossierProject.get(d.dossier_key), text(p.name), {
        role: text(p.role), influence: text(p.influence), interest: text(p.interest), position: text(p.position),
        strategy: text(p.strategy), cadence: text(p.cadence),
      }, false);
    }
    setRows(out);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const people = useMemo<StakeholderPerson[]>(() => {
    const byPerson = new Map<string, Array<StakeholderRole & { name: string }>>();
    for (const r of rows) {
      const k = norm(r.name);
      if (!byPerson.has(k)) byPerson.set(k, []);
      byPerson.get(k)!.push(r);
    }
    const list: StakeholderPerson[] = [];
    for (const [key, roles] of byPerson) {
      const influence = roles.reduce<Level>((best, r) => (r.influence && RANK[r.influence] > RANK[best] ? r.influence : best), 'low');
      const interest = roles.reduce<Level | null>((best, r) => (r.interest && (!best || RANK[r.interest] > RANK[best]) ? r.interest : best), null);
      const pos = roles.reduce<Position | null>((worst, r) => (r.position && (!worst || POSITION_RANK[r.position] > POSITION_RANK[worst]) ? r.position : worst), null);
      const counts = new Map<string, number>();
      for (const r of roles) if (r.role) counts.set(r.role, (counts.get(r.role) ?? 0) + 1);
      const primaryRole = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
      const projectIds = new Set(roles.map((r) => r.projectId));
      list.push({
        key, name: roles[0].name, influence, interest, position: pos, primaryRole, roles: roles.map(({ name: _n, ...rest }) => rest)
          .sort((a, b) => (a.portfolio ?? '').localeCompare(b.portfolio ?? '') || a.projectName.localeCompare(b.projectName)),
        projectCount: projectIds.size,
        portfolios: [...new Set(roles.map((r) => r.portfolio).filter((p): p is string => !!p))].sort(),
      });
    }
    return list.sort((a, b) => RANK[b.influence] - RANK[a.influence] || b.projectCount - a.projectCount || a.name.localeCompare(b.name));
  }, [rows]);

  const counts = useMemo(() => ({
    waiting: rows.filter((r) => !r.added).length,
    added: rows.filter((r) => r.added).length,
    projects: new Set(rows.map((r) => r.projectId)).size,
  }), [rows]);

  const addAll = useCallback(async () => {
    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('approve_all_project_intel', { p_kinds: ['stakeholder'] });
    if (rpcError) { setBusy(false); setError(rpcError.message); return null; }
    await load();
    setBusy(false);
    return data as { approved: number; projects: number };
  }, [load]);

  const undoAll = useCallback(async () => {
    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('undo_all_project_intel', { p_kinds: ['stakeholder'] });
    if (rpcError) { setBusy(false); setError(rpcError.message); return null; }
    await load();
    setBusy(false);
    return data as { reverted: number; skipped: number };
  }, [load]);

  return { people, counts, loading, busy, error, addAll, undoAll };
}
